import React, { useState, useEffect } from 'react';
import styles from './FreezePlayerModal.module.css';

/**
 * Modal wyboru trybu zamrożenia gracza w lidze grupowej.
 * - 'next' (domyślny, bezpieczny) — klasyczne zamrożenie od następnej rundy.
 * - 'now' — zamrożenie w bieżącej rundzie: usuwa mecze i propozycje, przyznaje rekompensatę.
 *   Dostępne tylko gdy backend (preflight) na to pozwala.
 */
const FreezePlayerModal = ({ isOpen, playerName, groupLabel, preflight, loading, onCancel, onConfirm }) => {
    const [mode, setMode] = useState('next');

    useEffect(() => {
        if (isOpen) setMode('next');
    }, [isOpen]);

    if (!isOpen) return null;

    const canFreezeNow = !!preflight?.canFreezeNow;
    const currentRound = preflight?.currentRound ?? 0;
    const cascade = preflight?.cascadeFreezePlayers || [];
    const matches = preflight?.matchesToDelete ?? 0;
    const proposals = preflight?.proposalsToDelete ?? 0;

    const confirmLabel = mode === 'now'
        ? (matches > 0 ? `Zamroź i usuń ${matches} mecz(e/ów)` : 'Zamroź teraz')
        : 'Zamroź od następnej rundy';

    return (
        <div className={styles.overlay} onClick={onCancel}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.title}>
                    Zamrożenie: {playerName}{groupLabel ? ` (grupa ${groupLabel})` : ''}
                </h3>

                {loading ? (
                    <p className={styles.loading}>Sprawdzanie skutków zamrożenia…</p>
                ) : (
                    <div className={styles.options}>
                        <label className={`${styles.option} ${!canFreezeNow ? styles.optionDisabled : ''}`}>
                            <input
                                type="radio"
                                name="freezeMode"
                                value="now"
                                checked={mode === 'now'}
                                disabled={!canFreezeNow}
                                onChange={() => setMode('now')}
                            />
                            <span className={styles.optionBody}>
                                <strong>Zamroź teraz — runda {currentRound}</strong>
                                {canFreezeNow ? (
                                    <>
                                        <span className={styles.optionDesc}>
                                            Usunie {matches} mecz(e/ów) i {proposals} propozycj(e/i) terminów.
                                            Gracz otrzyma punkty rekompensaty za tę rundę.
                                        </span>
                                        {cascade.length > 0 && (
                                            <span className={styles.warning}>
                                                ⚠ Grupa {groupLabel} zostanie spauzowana — zamrozi to również:{' '}
                                                {cascade.map(c => c.name).join(', ')}.
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <span className={styles.blockReason}>{preflight?.blockReason}</span>
                                )}
                            </span>
                        </label>

                        <label className={styles.option}>
                            <input
                                type="radio"
                                name="freezeMode"
                                value="next"
                                checked={mode === 'next'}
                                onChange={() => setMode('next')}
                            />
                            <span className={styles.optionBody}>
                                <strong>Zamroź od następnej rundy ({currentRound + 1})</strong>
                                <span className={styles.optionDesc}>
                                    Klasyczne zamrożenie — zadziała przy przejściu do rundy {currentRound + 1}.
                                </span>
                            </span>
                        </label>
                    </div>
                )}

                <div className={styles.actions}>
                    <button type="button" className={styles.cancelButton} onClick={onCancel}>Anuluj</button>
                    <button
                        type="button"
                        className={mode === 'now' ? styles.dangerButton : styles.confirmButton}
                        disabled={loading}
                        onClick={() => onConfirm(mode)}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FreezePlayerModal;
